import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { catchError, finalize, interval, map, Observable, startWith, Subscription, switchMap } from "rxjs";
import { LoadingService, OVERLAY_POSITION } from "../../../core/ui/loading.service";
import { RequestService } from "../services/request.service";
import { VendorCatalogRequestWrite, VendorCatalogRequestMessageWrite, VendorCatalogRequest, VendorCatalogRequestMessage } from "../../../shared/models/request.model";
import { TableQueryParams } from "../../../shared/models/table-query-params.model";
import { UserStore } from "src/app/core/auth/user/store/user.store";
import { UserRole } from "src/app/core/auth/user/enum/user-role.enum";
import { normalizeQuillHtml } from "../services/quill-utils.service";

@Injectable({ providedIn: 'root' })
export class RequestStore {
  private loadingService = inject(LoadingService);
  private requestService = inject(RequestService);

  private userStore = inject(UserStore);

  openRequestCount: WritableSignal<number> = signal(0);
  unreadRequestMessageCount: WritableSignal<number> = signal(0);

  private openRequestsPollingSub?: Subscription;
  
 private unreadMessagesPollingSub?: Subscription;
 
  
loadPagedRequestsByParam(
  queryParams?: TableQueryParams,
  params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    includeOnlyLastMessage?: boolean;
  }): Observable<{ totalCount: number; items: VendorCatalogRequest[] }> {
    this.loadingService.show(OVERLAY_POSITION.COMPONENT);
    return this.requestService.fetchPagedRequests(queryParams, params).pipe(
      map(result => ({
        totalCount: result?.totalCount ?? 0,
        items: (result?.items ?? []).map(item => ({
          ...item,
          htmlContent: item?.htmlContent ? normalizeQuillHtml(item.htmlContent) : item?.htmlContent,
          messages: (item.messages ?? []).map(message => ({
            ...message,
            htmlContent: message?.htmlContent ? normalizeQuillHtml(message.htmlContent) : message?.htmlContent
          }))
        }))
      })),
      finalize(() => this.loadingService.hide(OVERLAY_POSITION.COMPONENT))
    );
  }

  loadRequestsById(requestId: number): Observable<VendorCatalogRequest> {
    this.loadingService.show();
    return this.requestService.fetchRequestById(requestId).pipe(
      map(result => ({
        ...result,
        htmlContent: normalizeQuillHtml(result.htmlContent)
      })),
      catchError((err) => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  loadRequestsByIntegrationId(integrationId: number, onlyOpenRequests: boolean): Observable<VendorCatalogRequest[]> {
    this.loadingService.show();
    return this.requestService
      .fetchRequestByIntegrationId(integrationId, onlyOpenRequests)
      .pipe(
        map(result => (result ?? []).map(item => ({
          ...item,
          htmlContent: item?.htmlContent ? normalizeQuillHtml(item.htmlContent) : item?.htmlContent
        }))),
        catchError((err) => {
          throw err;
        }),
        finalize(() => this.loadingService.hide())
      );
  }

  loadPreparedRequests(
    integrationId: number
  ): Observable<VendorCatalogRequest> {
    this.loadingService.show();
    return this.requestService.fetchPreparedRequest(integrationId).pipe(
      catchError((err) => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  createRequest(
    request: VendorCatalogRequestWrite
  ): Observable<VendorCatalogRequest> {
    this.loadingService.show();
    const { id, ...requestWithoutId } = request;
    const cleanedRequest: Omit<VendorCatalogRequestWrite, 'id'> = {
      ...requestWithoutId,
      htmlContent: normalizeQuillHtml(requestWithoutId.htmlContent),
    };
    return this.requestService.createRequest(cleanedRequest).pipe(
      catchError((err) => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  createRequestMessage(
    requestMessage: VendorCatalogRequestWrite,
    requestId: number
  ): Observable<VendorCatalogRequestMessageWrite> {
    this.loadingService.show();

    const cleanedMessage = {
      ...requestMessage,
      htmlContent: normalizeQuillHtml(requestMessage.htmlContent)
    };
    return this.requestService
      .createRequestMessage(cleanedMessage, requestId)
      .pipe(
        map(result => ({
          ...result,
          htmlContent: normalizeQuillHtml(result.htmlContent)
        })),
        catchError((err) => {
          throw err;
        }),
        finalize(() => this.loadingService.hide())
      );
  }

  markMessageAsRead(id: number): Observable<boolean> {
    return this.requestService.markMessageAsRead(id).pipe(
      catchError((err) => {
        throw err;
      })
    );
  }

  closeRequest(id: number, acceptClient: boolean | null = null): Observable<boolean> {
    return this.requestService.closeRequest(id, acceptClient).pipe(
      catchError((err) => {
        throw err;
      })
    );
  }

  reOpenRequest(id: number): Observable<boolean> {
    return this.requestService.reOpenRequest(id).pipe(
      catchError((err) => {
        throw err;
      })
    );
  }

  loadPagedRequestMessages(
    requestId: number,
    skip: number,
    take: number
  ): Observable<VendorCatalogRequestMessage[]> {
    return this.requestService
      .loadPagedRequestMessages(requestId, skip, take)
      .pipe(
        catchError((err) => {
          throw err;
        })
      );
  }

  loadFilterOptions(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    onlyAdmin?: boolean;
  }): Observable<{ clientNames: string[]; vendorNames: string[] }> {
    return this.requestService.fetchFilterOptions(params).pipe(
      catchError((err) => {
        throw err;
      })
    );
  }

  getOpenRequests(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    onlyAdmin?: boolean;
  }): Observable<number> {
    return this.requestService.getOpenRequestCount(params).pipe(
      catchError((err) => {
        throw err;
      })
    );
  }

  getUnreadRequestMessageCount(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
  }): Observable<number> {
    return this.requestService.getUnreadRequestMessageCount(params).pipe(
      catchError((err) => {
        throw err;
      })
    );
  }

   startOpenRequestsPolling(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    onlyAdmin?: boolean;
  }) {
    this.openRequestsPollingSub?.unsubscribe();

    this.openRequestsPollingSub = interval(15000)
      .pipe(
        startWith(0),
        switchMap(() => {
          const effectiveParams = { ...(params ?? {}) };

          if (this.userStore.hasRole(UserRole.Customer)) {
            effectiveParams.clientId = this.userStore.activeTenant()?.id;
          }

          return this.getOpenRequests(effectiveParams);
        })
      )
      .subscribe((count) => this.openRequestCount.set(count));
  }

  stopOpenRequestsPolling() {
    this.openRequestsPollingSub?.unsubscribe();
    this.openRequestsPollingSub = undefined;
  }


  loadUnreadRequestMessagesPeriodically(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
  }) {
    return interval(15000)
      .pipe(
        startWith(0),
        switchMap(() => this.getUnreadRequestMessageCount(params))
      )
      .subscribe((count) => this.unreadRequestMessageCount.set(count));
  }

  loadOpenRequestsOnce(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    onlyAdmin?: boolean;
  }) {
    this.getOpenRequests(params).subscribe((count) =>
      this.openRequestCount.set(count)
    );
  }


startUnreadRequestMessagesPolling(params?: {
  integrationId?: number;
  clientId?: number;
  vendorId?: number;
}) {
  this.unreadMessagesPollingSub?.unsubscribe();

  this.unreadMessagesPollingSub = interval(15000)
    .pipe(
      startWith(0),
      switchMap(() => this.getUnreadRequestMessageCount(params))
    )
    .subscribe((count) => this.unreadRequestMessageCount.set(count));
}

stopUnreadRequestMessagesPolling() {
  this.unreadMessagesPollingSub?.unsubscribe();
  this.unreadMessagesPollingSub = undefined;
}
}